import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { billingApi } from '../../api/billing';
import './BankDetailsForm.css';

/**
 * BankDetailsForm — BSB / Account Name / Account Number editor for bank
 * transfer. Always editable post-pivot (2026-05-10): bank transfer is a free
 * feature, so there is no longer a subscription gate that toggles read-only.
 */
const BSB_REGEX = /^\d{3}-\d{3}$/;
const ACCOUNT_NUMBER_REGEX = /^\d{6,10}$/;

const BankDetailsForm = ({ initialBankDetails, onSaved }) => {
    const [bsb, setBsb] = useState(initialBankDetails?.bsb || '');
    const [accountName, setAccountName] = useState(initialBankDetails?.accountName || '');
    const [accountNumber, setAccountNumber] = useState(initialBankDetails?.accountNumber || '');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        setBsb(initialBankDetails?.bsb || '');
        setAccountName(initialBankDetails?.accountName || '');
        setAccountNumber(initialBankDetails?.accountNumber || '');
    }, [initialBankDetails]);

    const validate = () => {
        if (!BSB_REGEX.test(bsb)) {
            return 'BSB must be in XXX-XXX format (digits only).';
        }
        if (!accountName || accountName.length > 100) {
            return 'Account name is required (up to 100 characters).';
        }
        if (!ACCOUNT_NUMBER_REGEX.test(accountNumber)) {
            return 'Account number must be 6 to 10 digits.';
        }
        return null;
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setError(null);
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        setIsSaving(true);
        try {
            await billingApi.saveBankDetails(bsb, accountName.trim(), accountNumber);
            toast.success('Bank details saved.');
            await onSaved?.();
        } catch (err) {
            const msg = err.message || 'Failed to save bank details.';
            setError(msg);
            toast.error(msg);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <form className="bank-details-form" onSubmit={handleSave}>
            <div className="bank-details-form-row">
                <div className="bank-details-form-field">
                    <label htmlFor="bank-details-bsb" className="form-label">BSB</label>
                    <p className="form-hint">Format: XXX-XXX (digits only).</p>
                    <input
                        id="bank-details-bsb"
                        type="text"
                        className="form-input"
                        placeholder="062-001"
                        value={bsb}
                        onChange={(e) => setBsb(e.target.value)}
                        disabled={isSaving}
                        inputMode="numeric"
                        maxLength={7}
                    />
                </div>

                <div className="bank-details-form-field">
                    <label htmlFor="bank-details-account-number" className="form-label">Account number</label>
                    <p className="form-hint">6 to 10 digits, no spaces or dashes.</p>
                    <input
                        id="bank-details-account-number"
                        type="text"
                        className="form-input"
                        placeholder="12345678"
                        value={accountNumber}
                        onChange={(e) => setAccountNumber(e.target.value)}
                        disabled={isSaving}
                        inputMode="numeric"
                        maxLength={10}
                    />
                </div>
            </div>

            <div className="bank-details-form-field">
                <label htmlFor="bank-details-account-name" className="form-label">Account name</label>
                <p className="form-hint">The exact name on your business bank account.</p>
                <input
                    id="bank-details-account-name"
                    type="text"
                    className="form-input"
                    placeholder="Westside Dance Studio"
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value)}
                    disabled={isSaving}
                    maxLength={100}
                />
            </div>

            {error && <p className="bank-details-form-error" role="alert">{error}</p>}

            <div className="bank-details-form-actions">
                <button type="submit" className="btn-purple" disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save bank details'}
                </button>
            </div>
        </form>
    );
};

export default BankDetailsForm;
